import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ items = [], index, onClose, onNav }) {
  const item = items[index];

  const prev = useCallback(
    () => onNav((index - 1 + items.length) % items.length),
    [index, items.length, onNav]
  );
  const next = useCallback(() => onNav((index + 1) % items.length), [index, items.length, onNav]);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose, item]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex flex-col"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={item.title || "Media viewer"}
        >
          <div className="flex justify-between items-center px-5 py-4 text-[11px] tracking-[0.25em] text-white/60">
            <span className="truncate pr-4">{item.title || ""}</span>
            <span data-testid="lightbox-counter">
              {index + 1} / {items.length}
            </span>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 pb-4 min-h-0">
            <motion.img
              key={item.id || index}
              src={item.url}
              alt={item.title || "Media"}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-4 p-3 text-white/70 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
          {items.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next image"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
